import React, { useContext, useLayoutEffect, useMemo, useState } from "react";
import {
  Grid,
  CardContent,
  Card,
  Tooltip,
  Paper,
  CardMedia,
  styled,
} from "@mui/material";
import { paletteVar, Typography } from "@platform/design-system";
import {
  CLIENT_ACTION_VALUES,
  CURRENT_COVERAGES,
  RFP_ACTION_VALUES,
} from "@platform/rfp-core";

import { NEW_COVERAGES, RFP_ACTION_TYPES } from "../constants";
import { Default, Dental } from "../../../assets/coverages";
import { RfpContext } from "../contexts";
import RfpRemoveCoverage from "../components/rfpRemoveCoverage";

const CoveragesGrid = styled(Grid)(({ theme }) => ({
  rowGap: theme.spacing(3),
  columnGap: theme.spacing(2),
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))",
  gridAutoRows: "1fr",
  cursor: "pointer",
}));

const CoveragesItemContainer = styled(CardMedia)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "&.selected": {
    boxShadow: `0 0 0 2px ${theme.palette.brandSupporting.light}`,
    ".MuiCardContent-root": {
      backgroundColor: paletteVar("surfaces-primary", "#EFF8FB"),
    },
  },
  ".MuiCardContent-root": {
    backgroundColor: paletteVar("surfaces-neutral", "#f2f2f2"),
  },
}));

const CoveragesItemMedia = styled(CardMedia)(({ theme }) => ({
  minHeight: theme.spacing(17),
  backgroundSize: "auto",
  flex: 2,
}));

const CoveragesItemContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1, 4),
  display: "flex",
  flex: 1,
  justifyContent: "center",
  alignItems: "center",
  minHeight: "4rem",
  "&:last-child": {
    paddingBottom: theme.spacing(1),
  },
  p: {
    textAlign: "center",
    color: theme.palette.text.primary,
  },
}));

const { CLIENT_TYPE } = CLIENT_ACTION_VALUES;
const { SELECTED_COVERAGES, HIGHER_LAST_STEP, ID } = RFP_ACTION_VALUES;
const { UPDATE_SELECTED_COVERAGES, UPDATE_COVERAGE_INDEX } = RFP_ACTION_TYPES;
const CoverageGrid = ({
  title,
  selected,
  data,
  handleSelection,
  isCurrentCoverage,
}) => {
  const coverageList = useMemo(() => {
    const dentalList = data.filter((value) =>
      value.name?.toLowerCase().includes("dental"),
    );
    const otherList = data.filter(
      (value) => !value.name?.toLowerCase().includes("dental"),
    );
    return [...dentalList, ...otherList];
  }, [data]);

  return (
    <Grid container component={Paper} mb={3} padding={5} size={12}>
      <Grid size={12} mb={3}>
        <Typography variant="header-large">{title}</Typography>
      </Grid>
      <CoveragesGrid container size={12}>
        {coverageList.map((coverage) => {
          const isSelected = !!selected?.some((v) => v.name === coverage.name);

          const clearImageName = coverage.name.replace(/[\s|&^/]/g, "");
          let image;

          try {
            image = new URL(
              `../../../assets/coverages/${clearImageName}.svg`,
              import.meta.url,
            ).href;
            if (image.endsWith("undefined")) {
              image = clearImageName?.toLowerCase().includes("dental")
                ? Dental
                : Default;
            }
          } catch {
            image = Default;
          }

          return (
            <CoveragesItemContainer
              key={coverage.name}
              component={Card}
              data-testid={`coverage-card-${coverage.name}`}
              className={`${isSelected ? "selected" : null}`}
              onClick={() =>
                handleSelection({ ...coverage, isCurrentCoverage })
              }
            >
              <CoveragesItemMedia image={image} />
              <CoveragesItemContent>
                <Tooltip title={coverage.name}>
                  <Typography>{coverage.name}</Typography>
                </Tooltip>
              </CoveragesItemContent>
            </CoveragesItemContainer>
          );
        })}
      </CoveragesGrid>
    </Grid>
  );
};

const CoverageSelection = ({ px }) => {
  const { form, rfp, setRfp } = useContext(RfpContext);
  const currHigherStep = rfp[HIGHER_LAST_STEP] + 1;
  const selectedCoverages = rfp[SELECTED_COVERAGES];
  const [selectedItem, setSelectedItem] = useState(null);

  const updateSelection = (value) => {
    setRfp({ type: UPDATE_SELECTED_COVERAGES, value });
  };

  useLayoutEffect(() => {
    setRfp({ type: UPDATE_COVERAGE_INDEX, value: 0 });
  }, [setRfp]);

  const handleSubmitDelete = () => {
    updateSelection(selectedItem);
    handleCloseConfirmation();
  };

  const handleCloseConfirmation = () => {
    setSelectedItem(null);
  };

  const handleSelection = (value) => {
    const existInSelectedCovs = selectedCoverages.some(
      (item) => item.name === value.name,
    );
    if (existInSelectedCovs && currHigherStep > 2) {
      setSelectedItem(value);
    } else {
      updateSelection(value);
    }
  };

  return (
    <Grid container px={px} size={12}>
      {!!selectedItem && (
        <RfpRemoveCoverage
          isOpen={!!selectedItem}
          onClose={handleCloseConfirmation}
          onCancel={handleCloseConfirmation}
          onSubmit={handleSubmitDelete}
          coverageName={selectedItem?.name}
          rfpId={rfp[ID]}
          clientType={form[CLIENT_TYPE]}
        />
      )}
      <Typography variant="display-medium" margin="0 auto 2.5rem">
        Select Coverages To Quote
      </Typography>
      {!!rfp[CURRENT_COVERAGES]?.length && (
        <CoverageGrid
          title="Current Coverages"
          selected={selectedCoverages}
          data={rfp[CURRENT_COVERAGES]}
          handleSelection={handleSelection}
          isCurrentCoverage={true}
        />
      )}
      {!!rfp[NEW_COVERAGES].length && (
        <CoverageGrid
          title="New Coverages"
          selected={selectedCoverages}
          data={rfp[NEW_COVERAGES]}
          handleSelection={handleSelection}
          isCurrentCoverage={false}
        />
      )}
    </Grid>
  );
};

export default CoverageSelection;
